/**
 * Users & Agents — typed API helpers + TanStack Query hooks.
 *
 * Backend routes live in `backend/src/routes/admin-users-agents.ts`. The
 * Feedback tab calls into the existing `/v1/admin/feedback*` endpoints
 * implemented in `backend/src/routes/admin-feedback.ts`.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import { api } from '@/api/client';

// ---------------- Shared list-response shape -----------------

export interface ListMeta {
  count: number;
  total: number;
  limit: number;
  offset: number;
}

export interface ListResponse<T> {
  data: T[];
  meta: ListMeta;
}

// ---------------- People -----------------
export interface AdminPersonRow {
  id: string; user_id: string | null; identity_kind: 'account' | 'unfinished_lead';
  prediction_handle: string | null; email: string | null; email_verified: boolean;
  created_via?: string | null; created_at: string; last_seen_at: string | null;
  connected_agent_count?: number | null; first_auth_provider?: string | null;
  self_reported_model?: string | null; self_reported_provider?: string | null;
  self_reported_model_status?: 'reported' | 'unknown' | 'unwilling' | 'not_reported' | null;
  self_reported_model_reported_at?: string | null;
}
export function useAdminPeople(filters: {q?: string; limit: number; offset: number}) {
  return useQuery({queryKey:['admin','people',filters],queryFn:()=>api.get<ListResponse<AdminPersonRow>>(buildPath('/v1/admin/users-agents/people',filters)),placeholderData:keepPreviousData});
}

// ---------------- Users -----------------

/** Auth-event provider — kept in sync with the backend writer (issue #371). */
export type AuthEventProvider =
  | 'email_otp'
  | 'magic_link'
  | 'google'
  | 'apple'
  | 'dev_auth'
  | 'unknown';

export type AuthEventOutcome = 'success' | 'failure' | 'blocked' | 'unknown';

/** Queryable auth risk flags surfaced on the Users list + drawer. */
export type AuthRiskFlag = 'many_failures' | 'success_after_failures' | 'no_successful_login';

export interface AdminUserRow {
  id: string;
  lead_id: string;
  created_via?: string | null;
  connected_agent_count?: number | null;
  first_auth_provider?: string | null;
  email: string | null;
  display_name: string | null;
  agent_name: string | null;
  prediction_handle: string | null;
  email_verified: boolean;
  /** Deprecated alias from the old backend/admin wording. */
  verified_human?: boolean;
  created_at: string;
  onboarding_completed: boolean;
  agent_count: number;
  onboarding_session_count: number;
  elevenlabs_session_count: number | null;
  entry_count: number;
  last_active_at: string | null;
  // Auth-history fields (issue #371). `last_active_at` stays for back-compat.
  last_login_at: string | null;
  last_login_method: AuthEventProvider | null;
  last_failed_login_at: string | null;
  failures_24h: number;
  successes_24h: number;
  auth_risk_flags: AuthRiskFlag[];
  latest_chat_thread_id: string | null;
  latest_chat_started_at: string | null;
  latest_chat_last_activity_at: string | null;
  latest_chat_status: string | null;
  latest_chat_message_count: number | null;
  latest_chat_agent_id: string | null;
  latest_chat_game_key: string | null;
}

export interface UsersFilters {
  q?: string;
  onboarding?: 'completed' | 'in_progress' | 'any';
  has_agents?: 'yes' | 'no' | 'any';
  /** Auth-history filter (issue #371). */
  auth?: 'recent_failures' | 'no_success' | 'suspicious' | 'any';
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export function useAdminUsers(filters: UsersFilters) {
  return useQuery({
    queryKey: ['admin', 'users', filters],
    queryFn: () =>
      api.get<ListResponse<AdminUserRow>>(buildPath('/v1/admin/users-agents/users', filters)),
    placeholderData: keepPreviousData,
  });
}

export interface AdminUserDetail {
  created_via?: string | null;
  connected_agent_count?: number | null;
  first_auth_provider?: string | null;
  id: string;
  email: string | null;
  prediction_handle: string | null;
  agent_name: string | null;
  supabase_uid: string;
  email_verified: boolean;
  /** Deprecated alias from the old backend/admin wording. */
  verified_human?: boolean;
  email_verified_at: string | null;
  created_at: string;
  updated_at: string;
  agents: Array<{
    id: string;
    name: string;
    slug: string | null;
    wallet_address: string;
    created_at: string;
    updated_at: string;
    entry_count: number;
    result_count: number;
    feedback_count: number;
    draft_total_cost: number | null;
    draft_updated_at: string | null;
  }>;
  onboarding_sessions: Array<{
    id: string;
    status: string;
    started_at: string;
    last_activity_at: string;
    finished_at: string | null;
    created_agent_id: string | null;
  }>;
  chat_threads: Array<{
    id: string;
    kind: string;
    game_key: string;
    status: string;
    agent_id: string | null;
    agent_name: string | null;
    started_at: string;
    last_activity_at: string;
    finished_at: string | null;
    message_count: number;
    elevenlabs_conversation_id: string | null;
  }>;
  entries: Array<{
    id: string;
    agent_id: string;
    competition_id: string;
    competition_title: string;
    competition_status: string;
    competition_format: string;
    status: string;
    created_at: string;
  }>;
  feedback_count: number;
  // Auth-history block (issue #371).
  auth: {
    last_login_at: string | null;
    last_login_method: AuthEventProvider | null;
    last_failed_login_at: string | null;
    failures_24h: number;
    successes_24h: number;
    distinct_recent_ips: number;
    risk_flags: AuthRiskFlag[];
  };
}

export function useAdminUser(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => api.get<AdminUserDetail>(`/v1/admin/users-agents/users/${id}`),
    enabled: !!id,
  });
}

// ---------------- User auth events (issue #371) -----------------

export interface AdminUserAuthEventRow {
  id: string;
  occurred_at: string;
  action: string;
  outcome: AuthEventOutcome;
  provider: AuthEventProvider | null;
  reason_code: string | null;
  country: string | null;
  user_agent: string | null;
  request_id?: string | null;
  anon_id?: string | null;
  /** Present on the full timeline endpoint; absent on the drawer preview. */
  email?: string | null;
  user_id?: string | null;
  ip_hash?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface UserAuthEventsFilters {
  outcome?: AuthEventOutcome | 'any';
  action?: string;
  limit?: number;
  offset?: number;
}

export function useAdminUserAuthEvents(id: string | null, filters: UserAuthEventsFilters) {
  return useQuery({
    queryKey: ['admin', 'user', id, 'auth-events', filters],
    queryFn: () =>
      api.get<ListResponse<AdminUserAuthEventRow>>(
        buildPath(`/v1/admin/users-agents/users/${id}/auth-events`, filters),
      ),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

// ---------------- Agents -----------------

export type AdminAgentType = 'human_owned' | 'byoa' | 'archived_merge_artifact';
export type AdminAgentLifecycleStatus = 'active' | 'archived_merge_artifact';

export interface AdminAgentRow {
  id: string;
  lead_id: string | null;
  name: string;
  slug: string | null;
  wallet_address: string;
  type: AdminAgentType;
  lifecycle_status: AdminAgentLifecycleStatus;
  merged_into_agent_id: string | null;
  game_key: string | null;
  owner_user_id: string | null;
  owner_email: string | null;
  owner_prediction_handle: string | null;
  owner_agent_name: string | null;
  owner_email_verified: boolean;
  /** Deprecated alias from the old backend/admin wording. */
  verified_human?: boolean;
  created_at: string;
  updated_at: string;
  last_active_at: string | null;
  entry_count: number;
  total_points: number;
  latest_chat_thread_id: string | null;
  latest_chat_started_at: string | null;
  latest_chat_last_activity_at: string | null;
  latest_chat_status: string | null;
  latest_chat_message_count: number | null;
  latest_chat_game_key: string | null;
}

export interface AgentsFilters {
  q?: string;
  type?: AdminAgentType | 'any';
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export function useAdminAgents(filters: AgentsFilters) {
  return useQuery({
    queryKey: ['admin', 'agents', filters],
    queryFn: () =>
      api.get<ListResponse<AdminAgentRow>>(buildPath('/v1/admin/users-agents/agents', filters)),
    placeholderData: keepPreviousData,
  });
}

export interface AdminAgentDetail {
  id: string;
  name: string;
  manager_name: string | null;
  agent_row_name: string;
  slug: string | null;
  wallet_address: string;
  type: AdminAgentType;
  lifecycle_status: AdminAgentLifecycleStatus;
  merged_into_agent_id: string | null;
  game_key: string | null;
  owner_user_id: string | null;
  owner_email: string | null;
  owner_prediction_handle: string | null;
  owner_agent_name: string | null;
  owner_email_verified: boolean;
  /** Deprecated alias from the old backend/admin wording. */
  verified_human?: boolean;
  created_at: string;
  updated_at: string;
  profile: {
    manager_md: string | null;
    owner_md: string | null;
    tasks_md: string | null;
    boundaries_md: string | null;
  };
  draft: {
    total_cost: number | null;
    updated_at: string | null;
    fantasy_enabled: boolean;
    game_status: string;
    players: Array<{
      player_id: string;
      name: string | null;
      team_name: string | null;
      position: string | null;
      cost: number | null;
      is_captain: boolean;
      is_starter: boolean;
      bench_order: number | null;
    }>;
  };
  entry_count: number;
  result_count: number;
  feedback_count: number;
  total_points: number;
  entries: Array<{
    id: string;
    competition_id: string;
    competition_title: string;
    competition_status: string;
    competition_format: string;
    status: string;
    created_at: string;
  }>;
}

export function useAdminAgent(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'agent', id],
    queryFn: () => api.get<AdminAgentDetail>(`/v1/admin/users-agents/agents/${id}`),
    enabled: !!id,
  });
}

// ---------------- Predictions marketing funnel -----------------

/** Closed funnel-stage vocabulary (matches the backend `FunnelStage`). */
export type FunnelStage =
  | 'landing_visit'
  | 'landing_cta_clicked'
  | 'handle_chosen'
  | 'agent_deployed'
  | 'onboarding_chat_started'
  | 'supported_nation_saved'
  | 'prediction_first_made'
  | 'group_membership_started'
  | 'arena_viewed'
  | 'email_submitted'
  | 'account_linked'
  | 'email_verified';

/** The ranked funnel stages, lowest→highest. */
export const FUNNEL_ACQUISITION_STAGES: { stage: FunnelStage; label: string }[] = [
  { stage: 'landing_visit', label: 'Landing' },
  { stage: 'landing_cta_clicked', label: 'CTA clicked' },
  { stage: 'handle_chosen', label: 'Team name' },
  { stage: 'agent_deployed', label: 'Team created' },
  { stage: 'onboarding_chat_started', label: 'Onboarding' },
  { stage: 'supported_nation_saved', label: 'Country' },
  { stage: 'prediction_first_made', label: 'Prediction' },
  { stage: 'group_membership_started', label: 'Group' },
  { stage: 'arena_viewed', label: 'Arena' },
  { stage: 'email_submitted', label: 'Email submitted' },
  { stage: 'account_linked', label: 'Account' },
  { stage: 'email_verified', label: 'Email verified' },
];

/** One row per canonical visitor (anon→claimed identity merged). */
export interface AdminFunnelLeadRow {
  visitor_id: string;
  first_seen: string | null;
  last_seen: string | null;
  last_activity_at: string | null;
  last_activity_source: string | null;
  furthest_rank: number;
  furthest_acquisition_stage: string | null;
  account_started: boolean;
  account_started_at: string | null;
  claimed: boolean;
  claimed_at: string | null;
  email_verified: boolean;
  email_verified_at: string | null;
  /** Deprecated aliases kept for staggered backend/admin deploys. */
  verified_human?: boolean;
  verified_human_at?: string | null;
  in_group: boolean;
  prediction_count: number;
  rounds_predicted: number;
  manager_chat_count: number;
  app_open_count: number;
  invite_sent_count: number;
  is_likely_bot: boolean;
  user_id: string | null;
  agent_id: string | null;
  country: string | null;
  prediction_handle: string | null;
  email: string | null;
  agent_name: string | null;
  latest_agent_name: string | null;
  group_title: string | null;
  latest_chat_thread_id: string | null;
  latest_chat_started_at: string | null;
  latest_chat_last_activity_at: string | null;
  latest_chat_status: string | null;
  latest_chat_message_count: number;
  first_utm_source: string | null;
  first_utm_medium: string | null;
  first_utm_campaign: string | null;
  first_utm_content: string | null;
  first_utm_term: string | null;
  first_referrer: string | null;
  first_click_id_source: string | null;
  first_click_id_present: boolean;
}

export interface FunnelLeadsFilters {
  game?: string;
  stage?: string;
  lifecycle?: 'account' | 'pending_email_verification' | 'email_verified' | 'any';
  q?: string;
  country?: string;
  exclude_bots?: 'true' | 'false';
  from?: string;
  to?: string;
  sort?: FunnelLeadsSort;
  limit?: number;
  offset?: number;
}

export type FunnelLeadsSort =
  | 'first_seen_desc'
  | 'first_seen_asc'
  | 'last_activity_desc'
  | 'last_activity_asc'
  | 'furthest_stage_desc'
  | 'furthest_stage_asc'
  | 'lead_id_asc'
  | 'lead_id_desc'
  | 'handle_asc'
  | 'handle_desc'
  | 'agent_asc'
  | 'agent_desc'
  | 'account_asc'
  | 'account_desc'
  | 'prediction_count_asc'
  | 'prediction_count_desc'
  | 'group_asc'
  | 'group_desc'
  | 'engagement_asc'
  | 'engagement_desc'
  | 'country_asc'
  | 'country_desc';

export function useAdminFunnelLeads(filters: FunnelLeadsFilters) {
  return useQuery({
    queryKey: ['admin', 'funnel-leads', filters],
    queryFn: () =>
      api.get<ListResponse<AdminFunnelLeadRow>>(
        buildPath('/v1/admin/users-agents/funnel-leads', filters),
      ),
    placeholderData: keepPreviousData,
  });
}

export interface AdminFunnelEventRow {
  id: string;
  occurred_at: string | null;
  stage: string;
  event_source?: string;
  event_family?: string;
  platform?: string;
  funnel_step?: string | null;
  funnel_rank?: number | null;
  vendor_eligible?: boolean;
  delivery_status?: string | null;
  game_key: string;
  user_id: string | null;
  agent_id: string | null;
  competition_id: string | null;
  country: string | null;
  user_agent: string | null;
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  metadata: unknown;
  request_id: string | null;
}

export function useAdminFunnelLeadEvents(visitorId: string | null) {
  return useQuery({
    queryKey: ['admin', 'funnel-lead-events', visitorId],
    enabled: !!visitorId,
    queryFn: () =>
      api.get<{ data: AdminFunnelEventRow[]; meta: { count: number } }>(
        `/v1/admin/users-agents/funnel-leads/${visitorId}/events`,
      ),
  });
}

export interface AnalyticsAuditFilters {
  q?: string;
  event_id?: string;
  visitor_id?: string;
  event_name?: string;
  event_source?: string;
  event_family?: string;
  funnel_rank?: number | string;
  vendor_eligible?: 'true' | 'false' | 'any';
  delivery_status?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface AdminAnalyticsAuditRow {
  id: string;
  occurred_at: string | null;
  event_name: string;
  event_source: string;
  event_family: string;
  app_surface: string;
  platform: string;
  game_key: string;
  visitor_id: string;
  user_id: string | null;
  agent_id: string | null;
  competition_id: string | null;
  funnel_step: string | null;
  funnel_rank: number | null;
  vendor_eligible: boolean;
  consent_state: string | null;
  delivery_status: string;
  delivery_attempts: number;
  delivered_at: string | null;
  country: string | null;
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  first_utm_source: string | null;
  first_utm_medium: string | null;
  first_utm_campaign: string | null;
  marketing_channel: string | null;
  marketing_campaign_id: string | null;
  marketing_adset_id: string | null;
  marketing_ad_id: string | null;
  marketing_network: string | null;
  marketing_placement: string | null;
  click_id_source: string | null;
  metadata: unknown;
}

export function useAdminAnalyticsAudit(filters: AnalyticsAuditFilters) {
  return useQuery({
    queryKey: ['admin', 'analytics-audit', filters],
    queryFn: () =>
      api.get<ListResponse<AdminAnalyticsAuditRow>>(
        buildPath('/v1/admin/users-agents/analytics-events', filters),
      ),
    placeholderData: keepPreviousData,
  });
}

export interface AdminFunnelSummary {
  acquisition: { stage: string; visitors: number }[];
  milestones: {
    accounts?: number;
    claimed: number;
    email_verified?: number;
    verified_human?: number;
  };
  total_visitors: number;
}

export function useAdminFunnelSummary(
  filters: FunnelLeadsFilters,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['admin', 'funnel-summary', filters],
    queryFn: () =>
      api.get<AdminFunnelSummary>(buildPath('/v1/admin/users-agents/funnel-summary', filters)),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

export interface AdminFunnelSourceRow {
  granularity: FunnelSourceGranularity;
  source: string;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  network: string | null;
  placement: string | null;
  visitors: number;
  landing_visit: number;
  landing_cta_clicked: number;
  handle_chosen: number;
  agent_deployed: number;
  onboarding_chat_started: number;
  supported_nation_saved: number;
  prediction_first_made: number;
  reached_group: number;
  arena_viewed: number;
  arena_first_viewed: number;
  arena_returning_viewed: number;
  email_submitted: number;
  accounts: number;
  email_verified: number;
  /** Deprecated backend alias. */
  verified_accounts: number;
  conversion_to_verified: number;
}

export interface AdminFunnelCountryRow {
  country: string;
  landing_visit: number;
  handle_chosen: number;
  accounts: number;
  email_verified: number;
  spend_cents: number | null;
  cost_per_handle_cents: number | null;
}

export interface AdminAcquisitionOverview {
  summary: AdminFunnelSummary;
  data: AdminFunnelSourceRow[];
  meta: { count: number; granularity: string };
}

export type FunnelSourceGranularity =
  | 'source'
  | 'medium'
  | 'campaign'
  | 'adset'
  | 'ad'
  | 'content'
  | 'network'
  | 'placement';

type BackendFunnelSourceGranularity =
  | 'source'
  | 'medium'
  | 'campaign'
  | 'content'
  | 'network'
  | 'placement';

const FUNNEL_SOURCE_NUMERIC_FIELDS = [
  'visitors',
  'landing_visit',
  'landing_cta_clicked',
  'handle_chosen',
  'agent_deployed',
  'onboarding_chat_started',
  'supported_nation_saved',
  'prediction_first_made',
  'reached_group',
  'arena_viewed',
  'arena_first_viewed',
  'arena_returning_viewed',
  'email_submitted',
  'accounts',
  'email_verified',
  'verified_accounts',
] as const satisfies ReadonlyArray<keyof AdminFunnelSourceRow>;

type RawFunnelSourceRow = Partial<AdminFunnelSourceRow> & Record<string, unknown>;

const FUNNEL_LEADS_FALLBACK_PAGE_SIZE = 200;
const FUNNEL_LEADS_FALLBACK_MAX_ROWS = 20000;

const FUNNEL_STAGE_RANKS = FUNNEL_ACQUISITION_STAGES.reduce<Record<string, number>>(
  (ranks, stage, index) => {
    ranks[stage.stage] = index + 1;
    return ranks;
  },
  {},
);

function stringValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function numberValue(value: unknown): number {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

function fieldNumber(row: RawFunnelSourceRow, field: string, aliases: string[] = []): number {
  const keys = [field, ...aliases];
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      return numberValue(row[key]);
    }
  }
  return 0;
}

function referrerHost(referrer: string | null): string | null {
  const value = stringValue(referrer);
  if (!value) return null;

  try {
    return new URL(value).hostname || null;
  } catch {
    return null;
  }
}

function leadFunnelRank(lead: AdminFunnelLeadRow): number {
  const explicitRank = numberValue(lead.furthest_rank);
  if (explicitRank > 0) return explicitRank;
  return FUNNEL_STAGE_RANKS[lead.furthest_acquisition_stage ?? ''] ?? 0;
}

function leadReachedStage(lead: AdminFunnelLeadRow, stage: FunnelStage): boolean {
  const rank = FUNNEL_STAGE_RANKS[stage] ?? 0;
  return rank > 0 && leadFunnelRank(lead) >= rank;
}

function sourceKey(row: AdminFunnelSourceRow, granularity: FunnelSourceGranularity): string {
  return [
    row.source,
    granularity === 'source' ? null : row.medium,
    ['campaign', 'adset', 'ad', 'content', 'network', 'placement'].includes(granularity)
      ? row.campaign
      : null,
    ['adset', 'ad'].includes(granularity) ? row.term : null,
    ['ad', 'content'].includes(granularity) ? row.content : null,
    ['network', 'placement'].includes(granularity) ? row.network : null,
    granularity === 'placement' ? row.placement : null,
  ]
    .map((part) => part ?? '')
    .join('\u0000');
}

function normalizeFunnelSourceRow(
  row: RawFunnelSourceRow,
  granularity: FunnelSourceGranularity,
): AdminFunnelSourceRow {
  const source = stringValue(row.source) ?? 'direct';
  const medium = granularity === 'source' ? null : stringValue(row.medium);
  const campaign = ['campaign', 'adset', 'ad', 'content', 'network', 'placement'].includes(
    granularity,
  )
    ? stringValue(row.campaign)
    : null;
  const term = ['adset', 'ad', 'content'].includes(granularity) ? stringValue(row.term) : null;
  const content = ['ad', 'content'].includes(granularity) ? stringValue(row.content) : null;
  const network = ['network', 'placement'].includes(granularity) ? stringValue(row.network) : null;
  const placement = granularity === 'placement' ? stringValue(row.placement) : null;
  const visitors = fieldNumber(row, 'visitors');
  const landingVisit = fieldNumber(row, 'landing_visit', ['landing']) || visitors;
  const emailVerified = fieldNumber(row, 'email_verified', ['verified_accounts', 'verified_human']);

  return {
    granularity,
    source,
    medium,
    campaign,
    content,
    term,
    network,
    placement,
    visitors,
    landing_visit: landingVisit,
    landing_cta_clicked: fieldNumber(row, 'landing_cta_clicked', ['cta']),
    handle_chosen: fieldNumber(row, 'handle_chosen', ['handle']),
    agent_deployed: fieldNumber(row, 'agent_deployed', ['agent']),
    onboarding_chat_started: fieldNumber(row, 'onboarding_chat_started', ['onboarding']),
    supported_nation_saved: fieldNumber(row, 'supported_nation_saved', ['country']),
    prediction_first_made: fieldNumber(row, 'prediction_first_made', ['prediction']),
    reached_group: fieldNumber(row, 'reached_group', ['group_reached', 'group']),
    arena_viewed: fieldNumber(row, 'arena_viewed', ['arena']),
    arena_first_viewed: fieldNumber(row, 'arena_first_viewed'),
    arena_returning_viewed: fieldNumber(row, 'arena_returning_viewed'),
    email_submitted: fieldNumber(row, 'email_submitted', ['email']),
    accounts: fieldNumber(row, 'accounts', ['claimed', 'account_linked']),
    email_verified: emailVerified,
    verified_accounts: emailVerified,
    conversion_to_verified: landingVisit > 0 ? emailVerified / landingVisit : 0,
  };
}

export function aggregateFunnelSourceRows(
  rows: RawFunnelSourceRow[],
  granularity: FunnelSourceGranularity,
): AdminFunnelSourceRow[] {
  const grouped = new Map<string, AdminFunnelSourceRow>();

  for (const rawRow of rows) {
    const row = normalizeFunnelSourceRow(rawRow, granularity);
    const key = sourceKey(row, granularity);
    const current = grouped.get(key);

    if (!current) {
      grouped.set(key, { ...row });
      continue;
    }

    for (const field of FUNNEL_SOURCE_NUMERIC_FIELDS) {
      current[field] += row[field] as number;
    }

    if (current.content !== row.content) {
      current.content = null;
    }
    if (current.term !== row.term) {
      current.term = null;
    }
  }

  return [...grouped.values()]
    .map((row) => ({
      ...row,
      conversion_to_verified: row.landing_visit > 0 ? row.email_verified / row.landing_visit : 0,
    }))
    .sort((a, b) => b.landing_visit - a.landing_visit || b.visitors - a.visitors);
}

export function aggregateFunnelLeadRows(
  leads: AdminFunnelLeadRow[],
  granularity: FunnelSourceGranularity,
): AdminFunnelSourceRow[] {
  return aggregateFunnelSourceRows(
    leads.map((lead) => {
      const appOpenCount = numberValue(lead.app_open_count);
      const predictionCount = numberValue(lead.prediction_count);
      const claimed = Boolean(lead.claimed);
      const emailVerified = Boolean(lead.email_verified || lead.verified_human);
      const accountStarted = Boolean(lead.account_started);
      const arenaViewed = leadReachedStage(lead, 'arena_viewed') || appOpenCount > 0;
      const accountLinked = leadReachedStage(lead, 'account_linked') || claimed;
      const submittedEmail =
        leadReachedStage(lead, 'email_submitted') ||
        accountStarted ||
        accountLinked ||
        emailVerified;

      return {
        source: stringValue(lead.first_utm_source) ?? referrerHost(lead.first_referrer) ?? 'direct',
        medium: stringValue(lead.first_utm_medium),
        campaign: stringValue(lead.first_utm_campaign),
        term: stringValue(lead.first_utm_term),
        content: stringValue(lead.first_utm_content),
        network: null,
        placement: null,
        visitors: 1,
        landing_visit: 1,
        landing_cta_clicked: leadReachedStage(lead, 'landing_cta_clicked') ? 1 : 0,
        handle_chosen: leadReachedStage(lead, 'handle_chosen') ? 1 : 0,
        agent_deployed: leadReachedStage(lead, 'agent_deployed') ? 1 : 0,
        onboarding_chat_started: leadReachedStage(lead, 'onboarding_chat_started') ? 1 : 0,
        supported_nation_saved: leadReachedStage(lead, 'supported_nation_saved') ? 1 : 0,
        prediction_first_made:
          leadReachedStage(lead, 'prediction_first_made') || predictionCount > 0 ? 1 : 0,
        reached_group:
          leadReachedStage(lead, 'group_membership_started') || Boolean(lead.in_group) ? 1 : 0,
        arena_viewed: arenaViewed ? 1 : 0,
        arena_first_viewed: arenaViewed ? 1 : 0,
        arena_returning_viewed: appOpenCount > 1 ? 1 : 0,
        email_submitted: submittedEmail ? 1 : 0,
        accounts: accountLinked ? 1 : 0,
        email_verified: leadReachedStage(lead, 'email_verified') || emailVerified ? 1 : 0,
      };
    }),
    granularity,
  );
}

async function fetchAllAdminFunnelLeads(
  filters: FunnelLeadsFilters,
): Promise<AdminFunnelLeadRow[]> {
  const rows: AdminFunnelLeadRow[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;

  while (rows.length < Math.min(total, FUNNEL_LEADS_FALLBACK_MAX_ROWS)) {
    const response = await api.get<ListResponse<AdminFunnelLeadRow>>(
      buildPath('/v1/admin/users-agents/funnel-leads', {
        ...filters,
        sort: 'first_seen_desc',
        limit: FUNNEL_LEADS_FALLBACK_PAGE_SIZE,
        offset,
      }),
    );
    const batch = response.data ?? [];
    rows.push(...batch);

    const responseTotal = numberValue(response.meta?.total);
    if (responseTotal > 0) {
      total = responseTotal;
    }

    if (batch.length < FUNNEL_LEADS_FALLBACK_PAGE_SIZE) break;
    offset += batch.length;
  }

  return rows;
}

export function useAdminFunnelBySource(
  filters: FunnelLeadsFilters,
  granularity: FunnelSourceGranularity,
  options: { enabled?: boolean } = {},
) {
  const backendGranularity: BackendFunnelSourceGranularity =
    granularity === 'adset' || granularity === 'ad' ? 'content' : granularity;

  return useQuery({
    queryKey: ['admin', 'funnel-by-source', filters, granularity],
    queryFn: () =>
      api
        .get<{ data: AdminFunnelSourceRow[] }>(
          buildPath('/v1/admin/users-agents/funnel-by-source', {
            ...filters,
            source_granularity: backendGranularity,
          }),
        )
        .then((response) => ({
          data: aggregateFunnelSourceRows(response.data as RawFunnelSourceRow[], granularity),
        })),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

export function useAdminAcquisitionOverview(
  filters: Pick<FunnelLeadsFilters, 'game' | 'exclude_bots' | 'from' | 'to'>,
  granularity: FunnelSourceGranularity,
  options: { enabled?: boolean } = {},
) {
  const backendGranularity: BackendFunnelSourceGranularity =
    granularity === 'adset' || granularity === 'ad' ? 'content' : granularity;

  return useQuery({
    queryKey: ['admin', 'acquisition-overview', filters, granularity],
    queryFn: () =>
      api
        .get<AdminAcquisitionOverview>(
          buildPath('/v1/admin/users-agents/acquisition-overview', {
            ...filters,
            source_granularity: backendGranularity,
          }),
        )
        .then((response) => ({
          ...response,
          data: aggregateFunnelSourceRows(response.data as RawFunnelSourceRow[], granularity),
        })),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

export function useAdminFunnelByCountry(
  filters: Pick<FunnelLeadsFilters, 'game' | 'exclude_bots' | 'from' | 'to'>,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['admin', 'funnel-by-country', filters],
    queryFn: () =>
      api.get<{ data: AdminFunnelCountryRow[]; meta: { count: number } }>(
        buildPath('/v1/admin/users-agents/funnel-by-country', filters),
      ),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

export function useAdminFunnelBySourceLeadFallback(
  filters: FunnelLeadsFilters,
  granularity: FunnelSourceGranularity,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['admin', 'funnel-leads', 'lead-fallback', filters],
    queryFn: () => fetchAllAdminFunnelLeads(filters),
    select: (leads) => ({
      data: aggregateFunnelLeadRows(leads, granularity),
    }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

// ---------------- Chat logs -----------------

export interface ElevenLabsDeploymentSummary {
  id: string;
  agent_id: string;
  environment: string;
  product: string;
  dataset_key: string | null;
  role: string | null;
  git_sha: string | null;
  git_branch: string | null;
  git_dirty: boolean;
  pushed_by: string | null;
  prompt_sha256: string;
  prompt_chars: number;
  tools_sha256: string;
  tool_names: unknown;
  llm: string | null;
  temperature: number | null;
  max_tokens: number | null;
  voice_id: string | null;
  pushed_at: string;
  source_folder: string | null;
  source_persona: string | null;
}

export interface AdminChatPrivateCompetitionEntry {
  id: string;
  status: string;
  joined_at: string;
  agent_id: string;
  agent_name: string;
  competition_id: string;
  competition_title: string;
  competition_slug: string | null;
  competition_status: string;
  competition_visibility: string;
  competition_game_key: string;
}

export interface AdminChatLogRow {
  id: string;
  chat_thread_id?: string | null;
  session_type: 'conversation' | 'onboarding' | 'manager';
  user_email: string | null;
  prediction_handle: string | null;
  user_id: string | null;
  agent_id: string | null;
  agent_name: string | null;
  manager_name?: string | null;
  agent_row_name?: string | null;
  game_key?: string | null;
  anon_started_at: string | null;
  claimed_at: string | null;
  started_at: string;
  last_activity_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  turn_count: number | null;
  conversation_id: string | null;
  elevenlabs_deployment_id: string | null;
  elevenlabs_deployment: ElevenLabsDeploymentSummary | null;
  private_competition_entry_count: number;
  private_competition_entries: AdminChatPrivateCompetitionEntry[];
  status: string;
}

export interface AdminChatMessageRow {
  id: string;
  thread_id: string;
  role: 'user' | 'agent' | 'system' | 'tool' | string;
  text: string;
  source: string | null;
  elevenlabs_conversation_id: string | null;
  elevenlabs_deployment_id: string | null;
  elevenlabs_deployment: ElevenLabsDeploymentSummary | null;
  client_message_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface AdminChatLogMessagesResponse {
  thread: {
    id: string;
    user_id: string;
    user_email: string | null;
    prediction_handle: string | null;
    agent_id: string | null;
    agent_name: string | null;
    manager_name?: string | null;
    agent_row_name?: string | null;
    game_key: string;
    kind: string;
    status: string;
    elevenlabs_conversation_id: string | null;
    elevenlabs_deployment_id: string | null;
    elevenlabs_deployment: ElevenLabsDeploymentSummary | null;
    private_competition_entry_count: number;
    private_competition_entries: AdminChatPrivateCompetitionEntry[];
    started_at: string;
    last_activity_at: string;
    finished_at: string | null;
    message_count: number;
  } | null;
  data: AdminChatMessageRow[];
  meta: {
    count: number;
    limit: number;
    has_more: boolean;
  };
}

export interface ChatLogsFilters {
  q?: string;
  agent_id?: string;
  session_type?: 'onboarding' | 'manager' | 'any';
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export function useAdminChatLogs(filters: ChatLogsFilters) {
  return useQuery({
    queryKey: ['admin', 'chat-logs', filters],
    queryFn: () =>
      api.get<ListResponse<AdminChatLogRow>>(
        buildPath('/v1/admin/users-agents/chat-logs', filters),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useAdminChatLogMessages(id: string | null, limit = 50) {
  return useQuery({
    queryKey: ['admin', 'chat-log-messages', id, limit],
    queryFn: () =>
      api.get<AdminChatLogMessagesResponse>(
        `/v1/admin/users-agents/chat-logs/${id}/messages?limit=${limit}`,
      ),
    enabled: !!id,
  });
}

export function chatLogUserLabel(
  row: Pick<AdminChatLogRow, 'prediction_handle' | 'user_email'>,
): string | null {
  const handle = row.prediction_handle?.trim();
  if (handle) return handle;
  const email = row.user_email?.trim();
  return email || null;
}

export function adminAgentLifecycleLabel(
  status: AdminAgentRow['lifecycle_status'] | AdminAgentDetail['lifecycle_status'],
): string {
  if (status === 'archived_merge_artifact') return 'Archived / merged';
  return 'Active';
}

// ---------------- Feedback (existing /v1/admin/feedback) -----------------

export type FeedbackType = 'FEATURE_REQUEST' | 'BUG_REPORT' | 'FEEDBACK' | 'FRICTION';

export type FeedbackStatus = 'NEW' | 'ACCEPTED' | 'IN_PROGRESS' | 'FIXED' | 'IGNORED';

export interface AdminFeedbackRow {
  id: string;
  agent_id: string;
  agent_name: string;
  agent_wallet: string;
  type: FeedbackType;
  title: string;
  body: string;
  rating: number | null;
  metadata: Record<string, unknown> | null;
  /** Resolved reporter for rows attributed to the anonymous system agent
   *  (fantopy-spectator) whose metadata carries a user id. */
  reporter_user_id?: string | null;
  reporter_name?: string | null;
  /** Triage fields are absent until the backend triage migration is deployed. */
  status?: FeedbackStatus;
  triage_notes?: string | null;
  github_url?: string | null;
  triaged_at?: string | null;
  triaged_by?: string | null;
  created_at: string;
}

export interface AdminFeedbackStats {
  total: number;
  unresolved_count: number;
  by_type: Record<string, number>;
  by_status?: Record<string, number>;
  avg_rating: string | null;
}

export interface FeedbackFilters {
  type?: FeedbackType | 'any';
  status?: FeedbackStatus | 'any';
  agent_id?: string;
  limit?: number;
  offset?: number;
}

export interface FeedbackTriagePatch {
  status?: FeedbackStatus;
  triage_notes?: string | null;
  github_url?: string | null;
}

export function useAdminFeedback(filters: FeedbackFilters) {
  return useQuery({
    queryKey: ['admin', 'feedback', filters],
    queryFn: () =>
      api.get<{ data: AdminFeedbackRow[]; meta: ListMeta }>(
        buildPath('/v1/admin/feedback', filters),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useAdminFeedbackStats() {
  return useQuery({
    queryKey: ['admin', 'feedback', 'stats'],
    queryFn: () => api.get<AdminFeedbackStats>('/v1/admin/feedback/stats'),
  });
}

export function useUpdateAdminFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: FeedbackTriagePatch }) =>
      api.patch<AdminFeedbackRow>(`/v1/admin/feedback/${encodeURIComponent(id)}`, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
  });
}

// ---------------- helpers -----------------

export function buildPath(base: string, filters: object): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '' || v === 'any') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
